import React from "react";
import { parseErrorMessage } from "@/lib/parseErrorMessage";
import { useState, useMemo } from 'react';
import { Camera, Play, ImageOff, Check, MoreVertical, Unlink, Tag } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { GroupTypeBadge } from '@/components/creatives/GroupTypeBadge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Creative, calculateAspectRatioLabel } from '@/types/creative';

interface CreativeCardProps {
  creative: Creative;
  onClick: () => void;
  selectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  onRefresh?: () => void;
  /** Pre-resolved signed URL for the thumbnail (preferred over creative.thumbnail_url) */
  signedThumbnailUrl?: string | null;
}

const CreativeCard = ({ 
  creative, 
  onClick,
  selectionMode = false,
  isSelected = false,
  onToggleSelect,
  onRefresh,
  signedThumbnailUrl,
}: CreativeCardProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { toast } = useToast();

  const aspectRatioLabel = calculateAspectRatioLabel(creative.dimensions);
  const isVideo = creative.creative_type === 'video';

  // Tag badges
  const tagBadges = useMemo(() => {
    if (!creative.tags || typeof creative.tags !== 'object') return [];
    return Object.values(creative.tags).filter(v => v && v.trim());
  }, [creative.tags]);

  const truncatedFilename = creative.filename.length > 30
    ? `${creative.filename.substring(0, 27)}...`
    : creative.filename;

  const handleUngroup = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      const { error } = await supabase
        .from('creative_metadata')
        .update({ group_id: null, group_type: 'single' })
        .eq('id', creative.id);

      if (error) throw error;

      toast({
        title: 'Removed from group',
        description: `${creative.filename} is now a single asset`
      });

      onRefresh?.();
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: parseErrorMessage(error),
        variant: 'destructive'
      });
    }
  };

  return (
    <>
      <Card
        className={cn(
          'group cursor-pointer overflow-hidden transition-all duration-200',
          'hover:shadow-lg hover:scale-[1.02]',
          'border-border bg-card',
          isSelected && 'ring-2 ring-primary'
        )}
        onClick={onClick}
      >
        {/* Thumbnail Container */}
        <div className="relative aspect-square bg-muted overflow-hidden">
          {/* Selection checkbox overlay */}
          {selectionMode && (
            <div 
              className={cn(
                'absolute top-2 left-2 z-10 w-6 h-6 rounded border-2 flex items-center justify-center transition-colors',
                isSelected 
                  ? 'bg-primary border-primary' 
                  : 'bg-background/80 border-muted-foreground/50 hover:border-primary'
              )}
              onClick={(e) => {
                e.stopPropagation();
                onToggleSelect?.();
              }}
            >
              {isSelected && <Check className="h-4 w-4 text-primary-foreground" />}
            </div>
          )}

          {/* Actions menu for grouped creatives */}
          {creative.group_type && creative.group_type !== 'single' && !selectionMode && (
            <div className="absolute top-2 left-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-6 w-6 bg-background/80 hover:bg-background"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-popover">
                  <DropdownMenuItem onClick={handleUngroup}>
                    <Unlink className="h-4 w-4 mr-2" />
                    Remove from group
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Loading skeleton */}
          {!imageLoaded && !imageError && (
            <Skeleton className="absolute inset-0" />
          )}

          {/* Thumbnail image — prefer signed URL, fall back to legacy public URL */}
          {(signedThumbnailUrl || creative.thumbnail_url) && !imageError ? (
            <img
              src={signedThumbnailUrl || creative.thumbnail_url!}
              alt={creative.filename}
              className={cn(
                'w-full h-full object-cover transition-opacity duration-200',
                imageLoaded ? 'opacity-100' : 'opacity-0'
              )}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-muted">
              <ImageOff className="h-8 w-8 text-muted-foreground" />
            </div>
          )}

          {/* Badge overlays container */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Group badge - TOP RIGHT */}
            {creative.group_type && creative.group_type !== 'single' && creative.group_id && (
              <div className="absolute top-2 right-2 pointer-events-auto z-20">
                <GroupTypeBadge 
                  groupType={creative.group_type as 'single' | 'pair' | 'triple'}
                />
              </div>
            )}
            
            {/* Type badge (Video/Image) - BOTTOM RIGHT */}
            <Badge 
              variant="secondary" 
              className="absolute bottom-2 right-2 bg-black/70 text-white border-none pointer-events-auto"
            >
              {isVideo ? (
                <>
                  <Play className="h-3 w-3 mr-1" />
                  Video
                </>
              ) : (
                <>
                  <Camera className="h-3 w-3 mr-1" />
                  Image
                </>
              )}
            </Badge>
          </div>
        </div>

        {/* Card Footer */}
        <CardContent className="p-3 space-y-1">
          {/* Filename with tooltip */}
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="text-sm font-medium text-foreground truncate">
                {truncatedFilename}
              </p>
            </TooltipTrigger>
            {creative.filename.length > 30 && (
              <TooltipContent side="bottom" className="max-w-[300px]">
                <p className="break-all">{creative.filename}</p>
              </TooltipContent>
            )}
          </Tooltip>

          {/* Metadata row */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span className="capitalize">{creative.creative_type}</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help">{aspectRatioLabel}</span>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>{creative.dimensions || 'Unknown dimensions'}</p>
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Tag badges */}
          {tagBadges.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              <Tag className="h-3 w-3 text-muted-foreground shrink-0" />
              {tagBadges.slice(0, 3).map((val, i) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground truncate max-w-[70px]">
                  {val}
                </span>
              ))}
              {tagBadges.length > 3 && (
                <span className="text-[10px] text-muted-foreground">+{tagBadges.length - 3}</span>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default CreativeCard;
